import { useCallback } from 'react';
import { useRobot, useUI, useFile } from '../context/AppContext.jsx';
import { useFileHandlers } from './useFileHandlers.js';

export function useRobotConnection() {
  const {
    robotClient,
    robotFiles,
    setRobotFiles,
    robotConnection,
    setRobotConnection,
  } = useRobot();
  const { setShowRobotModal, setShowFileListModal, showStatus } = useUI();
  const { currentWaypoints } = useFile();
  const { handleFile, handleInstallationFile } = useFileHandlers();

  const connectToRobot = useCallback(async (host, username, password) => {
    try {
      showStatus('Connecting to robot...', 'info');
      const files = await robotClient.listFiles(host, username, password);
      setRobotFiles(files);
      setRobotConnection({ host, username, password, type: 'ssh' });
      setShowRobotModal(false);
      setShowFileListModal(true);
      showStatus('Connected successfully', 'success');
    } catch (error) {
      console.error('Connection error:', error);
      showStatus(`Connection failed: ${error.message}`, 'error');
      throw error;
    }
  }, [robotClient, setShowRobotModal, setShowFileListModal, showStatus, setRobotFiles, setRobotConnection]);

  const downloadFromRobot = useCallback(async (filename) => {
    try {
      showStatus(`Downloading ${filename}...`, 'info');
      const result = await robotClient.downloadFile(
        filename,
        robotConnection.host,
        robotConnection.username,
        robotConnection.password
      );

      const blob = new Blob([result.data], { type: 'application/octet-stream' });
      const file = new File([blob], filename, { type: 'application/octet-stream' });

      await handleFile(file);

      // Try to download installation file
      try {
        const installFilename = 'default.installation';
        const installResult = await robotClient.downloadInstallation(
          installFilename,
          robotConnection.host,
          robotConnection.username,
          robotConnection.password
        );

        const installBlob = new Blob([installResult.data], { type: 'application/octet-stream' });
        const installFile = new File([installBlob], installFilename, { type: 'application/octet-stream' });

        // Use current waypoints from context (handleFile already parsed and stored them)
        await handleInstallationFile(installFile, currentWaypoints);
      } catch (error) {
        console.log('Installation file not available:', error.message);
      }

      setShowFileListModal(false);
      showStatus(`Loaded ${filename} from robot`, 'success');
    } catch (error) {
      console.error('Download error:', error);
      showStatus(`Download failed: ${error.message}`, 'error');
    }
  }, [robotConnection, robotClient, currentWaypoints, handleFile, handleInstallationFile, setShowFileListModal, showStatus]);

  const uploadToRobot = useCallback(async (host, username, password, filename, fileData) => {
    try {
      showStatus(`Uploading ${filename}...`, 'info');
      await robotClient.uploadFile(filename, fileData, host, username, password);
      showStatus(`Uploaded ${filename} successfully`, 'success');
    } catch (error) {
      console.error('Upload error:', error);
      showStatus(`Upload failed: ${error.message}`, 'error');
      throw error;
    }
  }, [robotClient, showStatus]);

  const connectToRobotFTP = useCallback(async () => {
    try {
      showStatus('Connecting to robot via FTP...', 'info');
      const files = await robotClient.listFilesFTP();
      setRobotFiles(files);
      setRobotConnection({ host: 'FTP', username: '', password: '', type: 'ftp' });
      setShowRobotModal(false);
      setShowFileListModal(true);
      showStatus('Connected successfully via FTP', 'success');
    } catch (error) {
      console.error('FTP Connection error:', error);
      showStatus(`FTP Connection failed: ${error.message}`, 'error');
      throw error;
    }
  }, [robotClient, setShowRobotModal, setShowFileListModal, showStatus, setRobotFiles, setRobotConnection]);

  const downloadFromRobotFTP = useCallback(async (filename) => {
    try {
      showStatus(`Downloading ${filename} via FTP...`, 'info');
      const result = await robotClient.downloadFileFTP(filename);

      const blob = new Blob([result.data], { type: 'application/octet-stream' });
      const file = new File([blob], filename, { type: 'application/octet-stream' });

      await handleFile(file);

      // Try to download installation file
      try {
        const installFilename = 'default.installation';
        const installResult = await robotClient.downloadInstallationFTP(installFilename);

        const installBlob = new Blob([installResult.data], { type: 'application/octet-stream' });
        const installFile = new File([installBlob], installFilename, { type: 'application/octet-stream' });

        // Use current waypoints from context (handleFile already parsed and stored them)
        await handleInstallationFile(installFile, currentWaypoints);
      } catch (error) {
        console.log('Installation file not available:', error.message);
      }

      setShowFileListModal(false);
      showStatus(`Loaded ${filename} from robot via FTP`, 'success');
    } catch (error) {
      console.error('FTP Download error:', error);
      showStatus(`FTP Download failed: ${error.message}`, 'error');
    }
  }, [robotClient, currentWaypoints, handleFile, handleInstallationFile, setShowFileListModal, showStatus]);

  const uploadToRobotFTP = useCallback(async (filename, fileData) => {
    try {
      showStatus(`Uploading ${filename} via FTP...`, 'info');
      await robotClient.uploadFileFTP(filename, fileData);
      showStatus(`Uploaded ${filename} successfully via FTP`, 'success');
    } catch (error) {
      console.error('FTP Upload error:', error);
      showStatus(`FTP Upload failed: ${error.message}`, 'error');
      throw error;
    }
  }, [robotClient, showStatus]);

  return {
    connectToRobot,
    downloadFromRobot,
    uploadToRobot,
    connectToRobotFTP,
    downloadFromRobotFTP,
    uploadToRobotFTP,
    robotFiles,
    robotConnection,
  };
}

